import React, { useMemo } from 'react';
import { GreetResults, usePreviousGreets } from '~/App/Api/GreetResults';
import { useProfileEvents } from '~/App/Api/ProfileEvents';
import { SpinnerNew } from '~/App/Components/Shared/Spinner';
import type { GreetResult } from '~/types/api';

import { Profile } from '~/types/models';
import UserHistoryItem from './UserHistoryItem';

export interface UserHistoryProps {
  profile: Profile;
}

export type GreetResultWithOccurredAt = GreetResult & {
  occurred_at: string;
};

type GreetResultResponse = Omit<GreetResults, 'data'> & {
  data: GreetResultWithOccurredAt[];
};

export default function UserHistory({ profile }: UserHistoryProps) {
  const { isLoading: isPreviousGreetsLoading, data: previousResults } =
    usePreviousGreets({
      profileId: profile.id,
    });
  const { isLoading: isProfileEventsLoading, data: profileEvents } =
    useProfileEvents(profile.id);

  const previousGreetResults = useMemo(() => {
    if (!previousResults?.data) return [];

    const greets = previousResults as GreetResultResponse;

    return greets.data?.map((greet) => {
      greet.occurred_at = (greet.sent_at ?? greet.greet.updated_at) as string;

      return greet;
    });
  }, [previousResults]);

  const orderedUserHistory = useMemo(() => {
    const history = [...previousGreetResults, ...(profileEvents?.data ?? [])];

    return history.sort((a, b) => {
      return Date.parse(b.occurred_at) - Date.parse(a.occurred_at);
    });
  }, [previousGreetResults, profileEvents]);

  if (isProfileEventsLoading || isPreviousGreetsLoading) {
    return <SpinnerNew />;
  }

  if (
    previousResults?.data?.length === 0 &&
    profileEvents?.data?.length === 0
  ) {
    return (
      <div>
        You will see all of your previous Bonjoros sent to this contact here
      </div>
    );
  }

  return (
    <div className='text-base flex flex-col gap-y-4'>
      {orderedUserHistory.map((history) => (
        <UserHistoryItem key={history.id} history={history} />
      ))}
    </div>
  );
}
